/**
 * Recent API Errors diagnostic card.
 *
 * Surfaces three buffers:
 *   1. Last 50 server errors (route, timestamp, exception class, traceback).
 *   2. Last 20 heavy requests (slow or oversized responses) -- in-memory, resets on restart.
 *   3. Last 50 slow requests (duration only, >5s) -- persisted, survives a restart/deploy.
 *
 * One-click visibility into prod 500s and performance issues without log access.
 */

import { useState, Fragment } from 'react';
import { Loader2, Search, AlertTriangle, CheckCircle2, Clock, HardDrive } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { recentErrorsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function formatTs(isoStr) {
  if (!isoStr) return '--';
  try {
    return new Date(isoStr).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  } catch (_) {
    return isoStr;
  }
}

export function RecentApiErrorsPanel() {
  var [loading, setLoading] = useState(false);
  var [data, setData] = useState(null);
  var [expanded, setExpanded] = useState({});

  var load = async function () {
    setLoading(true);
    try {
      var res = await recentErrorsAPI.get();
      setData(res.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  var toggleExpand = function (idx) {
    setExpanded(function (prev) {
      var next = Object.assign({}, prev);
      next[idx] = !next[idx];
      return next;
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Recent API Errors</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-muted-foreground">
          In-memory ring buffer of the last 50 server errors. Shows route, timestamp,
          exception class, and traceback. Resets on server restart.
        </p>

        <Button onClick={load} disabled={loading} size="sm">
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Search className="h-4 w-4 mr-2" />
          )}
          Load Errors
        </Button>

        {data && (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm">
              {data.count === 0 ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-muted-foreground">No recent errors</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span>{data.count} error{data.count !== 1 ? 's' : ''} in buffer</span>
                </>
              )}
            </div>

            {data.errors && data.errors.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="py-1.5 pr-2 font-medium">Time</th>
                      <th className="py-1.5 pr-2 font-medium">Route</th>
                      <th className="py-1.5 pr-2 font-medium">Exception</th>
                      <th className="py-1.5 pr-2 font-medium">Message</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.errors.map(function (err, i) {
                      return (
                        <Fragment key={i}>
                          <tr
                            className="border-b border-border/50 cursor-pointer hover:bg-muted/50"
                            onClick={function () { toggleExpand(i); }}
                          >
                            <td className="py-1.5 pr-2 text-muted-foreground whitespace-nowrap">
                              {formatTs(err.timestamp)}
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              <Badge variant="outline" className="text-[10px]">{err.method}</Badge>
                              {' '}{err.route}
                            </td>
                            <td className="py-1.5 pr-2">
                              <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-[10px]">
                                {err.exception_class}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-2 truncate max-w-[300px]">
                              {err.message}
                            </td>
                          </tr>
                          {expanded[i] && err.traceback && (
                            <tr>
                              <td colSpan={4} className="py-2 px-2">
                                <pre className="text-[10px] bg-muted/50 p-2 rounded overflow-x-auto whitespace-pre-wrap max-h-[200px] overflow-y-auto">
                                  {err.traceback}
                                </pre>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Heavy Requests section */}
            {data.heavy_requests && data.heavy_requests.length > 0 && (
              <div className="mt-4 space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span>{data.heavy_count} heavy request{data.heavy_count !== 1 ? 's' : ''} (slow or oversized)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1.5 pr-2 font-medium">Time</th>
                        <th className="py-1.5 pr-2 font-medium">Route</th>
                        <th className="py-1.5 pr-2 font-medium">Status</th>
                        <th className="py-1.5 pr-2 font-medium">Duration</th>
                        <th className="py-1.5 pr-2 font-medium">Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.heavy_requests.map(function (h, i) {
                        return (
                          <tr key={i} className="border-b border-border/50">
                            <td className="py-1.5 pr-2 text-muted-foreground whitespace-nowrap">
                              {formatTs(h.timestamp)}
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              <Badge variant="outline" className="text-[10px]">{h.method}</Badge>
                              {' '}{h.route}
                            </td>
                            <td className="py-1.5 pr-2">
                              <Badge className={h.status_code >= 500
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-[10px]'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]'
                              }>
                                {h.status_code}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              {h.duration_ms >= 1000
                                ? (h.duration_ms / 1000).toFixed(1) + 's'
                                : Math.round(h.duration_ms) + 'ms'}
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              {h.response_size != null
                                ? (h.response_size >= 1048576
                                    ? (h.response_size / 1048576).toFixed(1) + ' MB'
                                    : (h.response_size / 1024).toFixed(0) + ' KB')
                                : '--'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Slow Requests section (persisted, survives restart -- CLAUDE.md prompt 15 FIX 2) */}
            {data.slow_requests && data.slow_requests.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="slow-requests-section">
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span>{data.slow_count} slow request{data.slow_count !== 1 ? 's' : ''} (&gt;5s, persisted)</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b text-left">
                        <th className="py-1.5 pr-2 font-medium">Time</th>
                        <th className="py-1.5 pr-2 font-medium">Route</th>
                        <th className="py-1.5 pr-2 font-medium">Status</th>
                        <th className="py-1.5 pr-2 font-medium">Duration</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.slow_requests.map(function (s, i) {
                        return (
                          <tr key={i} className="border-b border-border/50">
                            <td className="py-1.5 pr-2 text-muted-foreground whitespace-nowrap">
                              {formatTs(s.ts)}
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              <Badge variant="outline" className="text-[10px]">{s.method}</Badge>
                              {' '}{s.path}
                            </td>
                            <td className="py-1.5 pr-2">
                              <Badge className={s.status_code >= 500
                                ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 text-[10px]'
                                : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 text-[10px]'
                              }>
                                {s.status_code}
                              </Badge>
                            </td>
                            <td className="py-1.5 pr-2 font-mono">
                              {s.duration_ms >= 1000
                                ? (s.duration_ms / 1000).toFixed(1) + 's'
                                : Math.round(s.duration_ms) + 'ms'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
